import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
// Change from DotLottieReact to Lottie component
import Lottie, { LottieRefCurrentProps } from 'lottie-react'
// Import the JSON file instead of the .lottie file
import HorseAnimationData from '../../../../assets/Horse-Purple.json'
// Import racing pattern types and speed utilities
import type { RacingPattern } from '../../../../utils/racingPatternGenerator'
import {
  calculateCurrentSpeedMultiplier,
  type SpeedCalculationResult
} from '../../../../utils/speedVariationUtils'

// Define interfaces for the component props
interface CompetitorEvent {
  type: string
  coins: number
  positionTo: number
  time: number
}

interface Competitor {
  id: string
  name: string
  image: string
  points: number
  bugs: number
  role?: string
  coins?: number
  events?: CompetitorEvent[]
}

interface RacingCompetitorProps {
  competitor: Competitor
  yPos: number
  startX: number
  finishLineX: number
  isLiningUp: boolean
  isRacing: boolean
  winner: boolean
  entryDelay: number
  onFinish: (isWinner: boolean) => void
  borderColor?: string
  horseRgbValues?: number[]
  zIndex?: number
  racingPattern?: RacingPattern
}

interface LottieLayer {
  shapes?: Array<{
    it?: Array<{
      ty?: string
      c?: {
        k?: number[]
      }
    }>
  }>
  layers?: LottieLayer[]
}

interface LottieAsset {
  layers?: LottieLayer[]
}

interface LottieAnimationData {
  layers: LottieLayer[]
  assets?: LottieAsset[]
}

const RacingCompetitor: React.FC<RacingCompetitorProps> = React.memo(({
  competitor,
  yPos,
  startX,
  finishLineX,
  isLiningUp,
  isRacing,
  winner,
  entryDelay,
  onFinish,
  // Remove colorFilter prop
  borderColor = '#8a2be2', // Default purple border color
  horseRgbValues = [0.952941176471, 0.325490196078, 1, 1], // Default horse color (purple)
  zIndex = 1,
  racingPattern, // Accept racing pattern prop (will be used in task 4)
}) => {
  // Simplified isWinner logic - just use the winner prop directly
  const isWinner = winner

  // Wrap events in useMemo to prevent it from changing on every render
  const events = useMemo(() => competitor.events || [], [competitor.events])

  // Account for horse animation size
  const horseWidth = 160
  const horseOffset = horseWidth / 2

  // IMPORTANT: We need consistent handling of positions
  // - startX is the position where the CENTER of the horse should be at the start
  // - Framer Motion positions the LEFT edge of the component
  // - So we need to adjust initialX and currentX to account for this offset
  const initialPosition = startX - horseOffset
  const [currentX, setCurrentX] = useState<number>(initialPosition)
  const [currentText, setCurrentText] = useState<string>("")
  const [isMoving, setIsMoving] = useState<boolean>(false)

  // Speed variation state for dynamic movement
  const [currentSpeedMultiplier, setCurrentSpeedMultiplier] = useState<number>(1.0)
  const [raceStartTime, setRaceStartTime] = useState<number | null>(null)
  const [speedCalculationResult, setSpeedCalculationResult] = useState<SpeedCalculationResult | null>(null)

  // Debug state for development
  const [debugInfo, setDebugInfo] = useState<{
    segmentIndex: number
    segmentProgress: number
    easedProgress: number
    patternValid: boolean
  } | null>(null)

  // Use the correct LottieRefCurrentProps type imported from lottie-react
  const lottieRef = useRef<LottieRefCurrentProps>(null)
  const prevXRef = useRef<number>(initialPosition)

  // Refs for stable state management
  const timersRef = useRef<NodeJS.Timeout[]>([])
  const mountedRef = useRef<boolean>(true)
  const hasStartedRef = useRef<boolean>(false)
  const hasFinishedRef = useRef<boolean>(false)
  const speedUpdateIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const debugModeRef = useRef<boolean>(process.env.NODE_ENV === 'development')
  const lastLoggedSegmentRef = useRef<number>(-1)

  // Track component mount state and cleanup (optimized)
  useEffect(() => {
    mountedRef.current = true

    return () => {
      mountedRef.current = false
      // Clear all timers
      timersRef.current.forEach(timer => clearTimeout(timer))
      timersRef.current = []

      // Clear speed update interval
      if (speedUpdateIntervalRef.current) {
        clearInterval(speedUpdateIntervalRef.current)
        speedUpdateIntervalRef.current = null
      }

      // Reset state to prevent memory leaks
      setCurrentSpeedMultiplier(1.0)
      setSpeedCalculationResult(null)
      setDebugInfo(null)
      setCurrentText("")
      setIsMoving(false)
    }
  }, [competitor.name, events?.length])

  // Speed variation update effect - runs when racing with pattern (optimized)
  useEffect(() => {
    if (!isRacing || !racingPattern || !raceStartTime) {
      // Clear any existing speed update interval
      if (speedUpdateIntervalRef.current) {
        clearInterval(speedUpdateIntervalRef.current)
        speedUpdateIntervalRef.current = null
      }
      return
    }

    // Validate racing pattern before use
    const validatePattern = (pattern: RacingPattern): boolean => {
      if (!pattern.speedVariations || pattern.speedVariations.length === 0) {
        console.warn(`[${competitor.name}] Invalid racing pattern: no speed variations`)
        return false
      }

      // Check for valid speed multipliers
      for (const segment of pattern.speedVariations) {
        if (segment.speedMultiplier <= 0 || segment.speedMultiplier > 3) {
          console.warn(`[${competitor.name}] Invalid speed multiplier: ${segment.speedMultiplier}`)
          return false
        }
        if (segment.startTime >= segment.endTime) {
          console.warn(`[${competitor.name}] Invalid time segment: ${segment.startTime} >= ${segment.endTime}`)
          return false
        }
      }

      return true
    }

    const patternValid = validatePattern(racingPattern)

    // Update speed multiplier based on racing pattern at regular intervals
    const updateSpeedMultiplier = () => {
      if (!mountedRef.current || !raceStartTime || !patternValid) return

      const currentTime = Date.now() - raceStartTime
      const result = calculateCurrentSpeedMultiplier(racingPattern, currentTime)

      // Debug logging for speed pattern execution
      if (debugModeRef.current && result.currentSegment) {
        const segmentIndex = racingPattern.speedVariations.findIndex(
          seg => seg === result.currentSegment
        )

        // Log segment changes (optimized - only in development and with throttling)
        if (segmentIndex !== lastLoggedSegmentRef.current) {
          // Throttle logging to prevent performance issues - removed console.log for linting
          // Development logging can be re-enabled if needed for debugging
          lastLoggedSegmentRef.current = segmentIndex
        }

        // Update debug info
        setDebugInfo({
          segmentIndex,
          segmentProgress: result.segmentProgress,
          easedProgress: result.easedProgress,
          patternValid
        })
      } else if (debugModeRef.current && !result.currentSegment) {
        // Log when no segment is found (potential error) - only in development
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[${competitor.name}] No speed segment found for time ${currentTime}ms`)
        }
        setDebugInfo({
          segmentIndex: -1,
          segmentProgress: 0,
          easedProgress: 0,
          patternValid
        })
      }

      // Error reporting for invalid speed multipliers (optimized)
      if (result.speedMultiplier <= 0 || result.speedMultiplier > 3) {
        if (process.env.NODE_ENV === 'development') {
          console.error(`[${competitor.name}] Invalid speed multiplier calculated: ${result.speedMultiplier}`)
        }
        // Fallback to base speed
        setCurrentSpeedMultiplier(1.0)
        setSpeedCalculationResult(null)
        return
      }

      setCurrentSpeedMultiplier(result.speedMultiplier)
      setSpeedCalculationResult(result)
    }

    // Initial update
    updateSpeedMultiplier()

    // Set up interval for smooth speed transitions (optimized to 30 FPS for better performance)
    speedUpdateIntervalRef.current = setInterval(updateSpeedMultiplier, 33) // ~30 FPS for better performance

    return () => {
      if (speedUpdateIntervalRef.current) {
        clearInterval(speedUpdateIntervalRef.current)
        speedUpdateIntervalRef.current = null
      }
    }
  }, [isRacing, racingPattern, raceStartTime, competitor.name])

  // Initialize race start time when racing begins
  useEffect(() => {
    if (isRacing && !raceStartTime && !hasStartedRef.current) {
      setRaceStartTime(Date.now())
    } else if (!isRacing) {
      setRaceStartTime(null)
      setCurrentSpeedMultiplier(1.0)
      setSpeedCalculationResult(null)
    }
  }, [isRacing, raceStartTime, competitor.name])

  // Monitor position changes to determine if the horse is moving
  useEffect(() => {
    if (isRacing && currentX !== prevXRef.current) {
      setIsMoving(true)
      prevXRef.current = currentX

      // Calculate animation settle time based on speed multiplier
      // Faster speeds should settle quicker, slower speeds take longer
      const baseSettleTime = 500
      const adjustedSettleTime = baseSettleTime / Math.max(0.5, currentSpeedMultiplier)

      // After position change is complete, stop the movement animation
      const stopMovingTimer: NodeJS.Timeout = setTimeout(() => {
        if (mountedRef.current) {
          setIsMoving(false)
        }
      }, adjustedSettleTime)

      timersRef.current.push(stopMovingTimer)
    }

    if (!isRacing) {
      setIsMoving(false)
    }
  }, [currentX, isRacing, currentSpeedMultiplier])

  // Create a memoized version of the animation data with the custom color
  const animationData = useMemo<LottieAnimationData>(() => {
    // Deep clone the animation data to avoid modifying the original
    const modifiedData = JSON.parse(JSON.stringify(HorseAnimationData)) as LottieAnimationData

    // Function to recursively update colors in the Lottie JSON
    const updateColors = (layers?: LottieLayer[]): void => {
      if (!layers) return

      layers.forEach(layer => {
        // Update shapes with fill
        if (layer.shapes) {
          layer.shapes.forEach(shape => {
            if (shape.it) {
              shape.it.forEach(item => {
                // Find fill items and update their color values
                if (item.ty === "fl" && item.c && item.c.k) {
                  item.c.k = horseRgbValues
                }
              })
            }
          })
        }

        // Handle nested layers
        if (layer.layers) {
          updateColors(layer.layers)
        }
      })
    }

    // Update colors in the main layers
    updateColors(modifiedData.layers)

    // Update colors in assets (compositions)
    if (modifiedData.assets) {
      modifiedData.assets.forEach(asset => {
        if (asset.layers) {
          updateColors(asset.layers)
        }
      })
    }

    return modifiedData
  }, [horseRgbValues])

  // Control animation playback based on movement state and speed multiplier
  useEffect(() => {
    if (lottieRef.current) {
      try {
        if (isMoving) {
          lottieRef.current.play()
          // Adjust animation speed based on current speed multiplier
          // Clamp speed between 0.5 and 2.0 for realistic animation
          const animationSpeed = Math.max(0.5, Math.min(2.0, currentSpeedMultiplier))
          lottieRef.current.setSpeed(animationSpeed)
        } else {
          lottieRef.current.pause()
        }
      } catch (e) {
        console.error(`[${competitor.name}] Error controlling animation:`, e)
      }
    }
  }, [isMoving, currentSpeedMultiplier, competitor.name])

  // Sequential event execution function with speed variation support - wrapped in useCallback
  const executeEventSequence = useCallback((events: CompetitorEvent[], currentIndex: number = 0): void => {
    if (!mountedRef.current || !events || currentIndex >= events.length) return

    const event = events[currentIndex]
    const isLastEvent = currentIndex === events.length - 1

    // Calculate waiting time (relative to previous event)
    const prevEvent = currentIndex > 0 ? events[currentIndex - 1] : { time: 0 }
    const baseTimeToWait = (event.time - prevEvent.time) * 1000

    // Apply speed multiplier to timing if racing pattern is active
    let adjustedTimeToWait = baseTimeToWait
    if (racingPattern && raceStartTime) {
      const currentRaceTime = Date.now() - raceStartTime
      const speedResult = calculateCurrentSpeedMultiplier(racingPattern, currentRaceTime)

      // Inverse relationship: higher speed = less time to wait
      // Speed multiplier of 2.0 means half the time, 0.5 means double the time
      adjustedTimeToWait = baseTimeToWait / speedResult.speedMultiplier
    }

    const timer: NodeJS.Timeout = setTimeout(() => {
      if (!mountedRef.current) return

      // IMPORTANT: Event.positionTo is the CENTER of the horse
      // We need to adjust by horseOffset to get the left edge position
      const adjustedX = event.positionTo - horseOffset

      // Update position first
      setCurrentX(adjustedX)

      // Calculate text display timing based on speed multiplier
      // Faster speeds show text sooner, slower speeds delay text more
      const baseTextDelay = 50
      const speedAdjustedTextDelay = racingPattern ?
        baseTextDelay / Math.max(0.5, currentSpeedMultiplier) : baseTextDelay

      // Delay showing the text to match with motion animation
      const textTimer: NodeJS.Timeout = setTimeout(() => {
        if (mountedRef.current) {
          // Now show the text with the correct coin value from the event
          if (event.type === 'point') {
            setCurrentText(`+1 point`)
          } else if (event.type === 'bug') {
            setCurrentText(`+1 bug`)
          }

          // Text visibility duration also scales with speed
          const baseTextDuration = 300
          const speedAdjustedTextDuration = racingPattern ?
            baseTextDuration / Math.max(0.5, currentSpeedMultiplier) : baseTextDuration

          // Clear text after it's been visible for a moment
          const clearTextTimer: NodeJS.Timeout = setTimeout(() => {
            if (mountedRef.current) setCurrentText("")
          }, speedAdjustedTextDuration)
          timersRef.current.push(clearTextTimer)
        }
      }, speedAdjustedTextDelay)

      timersRef.current.push(textTimer)

      // Check for finish conditions - nose position matters for crossing finish
      // Nose position = left edge + full width (since horse faces left)
      const nosePosition = adjustedX + horseWidth
      const crossedFinish = nosePosition >= finishLineX

      if (isLastEvent) {
        if (!hasFinishedRef.current) {
          hasFinishedRef.current = true
          // For the winner, ensure they actually cross the finish line
          // For non-winners, use the original event position logic
          if (isWinner) {
            // Winner must actually cross the finish line
            onFinish(crossedFinish)
          } else {
            // Non-winners finish based on their event completion
            onFinish(false)
          }
        }
      }

      // Schedule next event
      executeEventSequence(events, currentIndex + 1)
    }, Math.max(10, adjustedTimeToWait)) // Ensure at least 10ms between events

    timersRef.current.push(timer)
  }, [finishLineX, horseOffset, horseWidth, isWinner, onFinish, racingPattern, raceStartTime, currentSpeedMultiplier])

  // Start the event sequence when racing begins
  useEffect(() => {
    if (isRacing && events && events.length > 0 && !hasStartedRef.current && !hasFinishedRef.current) {
      hasStartedRef.current = true
      executeEventSequence(events)
    }

    return () => {
      // If racing stops, clean up timers
      if (!isRacing && timersRef.current.length > 0) {
        timersRef.current.forEach(timer => clearTimeout(timer))
        timersRef.current = []
      }
    }
  }, [isRacing, events, competitor.name, isWinner, onFinish, finishLineX, executeEventSequence])

  return (
    <motion.div
      className="absolute"
      style={{
        top: yPos - 90, // Adjust for horse height
        zIndex: zIndex // Apply the z-index here
      }}
      initial={{ x: initialPosition - 100, opacity: 0 }} // Start 100px left of initial position
      animate={{
        x: isRacing ? currentX : initialPosition,
        opacity: 1
      }}
      transition={{
        x: {
          type: 'spring',
          stiffness: 100 * (isRacing ? currentSpeedMultiplier : 1),
          damping: 20,
          // Apply easing based on current speed calculation result
          ...(speedCalculationResult?.currentSegment && {
            ease: speedCalculationResult.currentSegment.easing === 'linear' ? 'linear' :
              speedCalculationResult.currentSegment.easing === 'ease-in' ? 'easeIn' :
                speedCalculationResult.currentSegment.easing === 'ease-out' ? 'easeOut' :
                  'easeInOut'
          })
        },
        opacity: { duration: 0.5 },
        delay: isLiningUp ? entryDelay : 0
      }}
    >
      <div className="relative">
        <div className="flex items-center">
          <div
            style={{
              width: `${horseWidth}px`,
              height: '160px',
              transform: 'scaleX(-1)',
              position: 'relative'
            }}
            className="relative horse-container"
          >
            <Lottie
              lottieRef={lottieRef}
              animationData={animationData}
              loop={true}
              autoplay={false}
              style={{
                width: '100%',
                height: '100%',
                position: 'relative',
                zIndex: 5,
              }}
            />

            {/* Competitor Photo */}
            {competitor.image && (
              <div
                className="absolute rounded-full overflow-hidden"
                style={{
                  width: '26px',
                  height: '26px',
                  top: '70px',
                  right: '90px',
                  border: `2px solid ${borderColor}`,
                  backgroundImage: `url(${competitor.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  transform: 'scaleX(-1)',
                  zIndex: 20,
                  boxShadow: `0 0 6px ${borderColor}` // Enhanced glow effect
                }}
              />
            )}
          </div>
        </div>

        <AnimatePresence>
          {currentText && (
            <motion.div
              className="absolute top-7 text-lg font-bold text-right caprasimo-regular"
              style={{
                color: currentText.includes('point') ? '#579a42' :
                  currentText.includes('bug') ? '#922133' : '#464646'
              }}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              key="text-animation"
            >
              {currentText}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Debug indicators - only visible in development */}
        {debugModeRef.current && debugInfo && isRacing && (
          <div
            className="absolute top-[-40px] left-0 text-xs bg-black bg-opacity-75 text-white p-1 rounded"
            style={{ fontSize: '10px', zIndex: 100 }}
          >
            <div>Speed: {currentSpeedMultiplier.toFixed(2)}x</div>
            <div>Segment: {debugInfo.segmentIndex}</div>
            <div>Progress: {(debugInfo.segmentProgress * 100).toFixed(0)}%</div>
            <div>Easing: {speedCalculationResult?.currentSegment?.easing || 'none'}</div>
            {!debugInfo.patternValid && (
              <div className="text-red-400">⚠ Invalid Pattern</div>
            )}
          </div>
        )}

        {/* Speed multiplier visual indicator - development only */}
        {debugModeRef.current && isRacing && (
          <div
            className="absolute top-[-60px] left-0 w-20 h-2 bg-gray-300 rounded"
            style={{ zIndex: 99 }}
          >
            <div
              className="h-full rounded transition-all duration-100"
              style={{
                width: `${Math.min(100, (currentSpeedMultiplier / 2) * 100)}%`,
                backgroundColor: currentSpeedMultiplier > 1.5 ? '#ef4444' :
                  currentSpeedMultiplier > 1.0 ? '#f59e0b' : '#10b981'
              }}
            />
          </div>
        )}
      </div>
    </motion.div>
  )
})

// Explicit name for the memoised component (fixes react/display-name)
RacingCompetitor.displayName = 'RacingCompetitor'

export default RacingCompetitor
